import { Injectable } from "@angular/core";
import { Adjuster, AdjusterInsert, AdjusterUpdate } from "../models/adjuster";
import { Carrier, CarrierInsert, CarrierUpdate } from "../models/carrier";
import { ApiService } from "@harlowe/core";
import { catchError, lastValueFrom, map, Observable, of } from "rxjs";
import { environment } from "../../environments/environment";
import { MessageService } from "primeng/api";
import { TealCompClaim, TealCompClaimInsert, TealCompClaimUpdate } from "../models/teal-comp-claim";
import { ErrorMessage } from "@harlowe/core";
import { instanceToInstance } from "class-transformer";
import { Store } from "@ngxs/store";
import { Claims } from "../state/claims/claim-actions";
import { TealCompEncounterHeader } from "../models/encounter";

export abstract class ClaimServiceBase
{
    abstract searchAdjusters(q: string): Promise<Adjuster[]>;   
    abstract searchCarriers(q: string): Promise<Carrier[]>;
    abstract createNewCarrier(carrier: CarrierInsert): Promise<Carrier | undefined>; 
    abstract createNewAdjuster(adjuster: AdjusterInsert): Promise<Adjuster | undefined>;
    abstract getClaim(claimId: number): Observable<TealCompClaim>;
    abstract createClaim(claim: TealCompClaimInsert): Promise<TealCompClaim | undefined>  
    abstract updateClaim(claimId: number, claim: TealCompClaimUpdate) : Promise<TealCompClaim | undefined>
    abstract updateCarrier(carrierId: number, carrier:CarrierUpdate) : Promise<Carrier | undefined>
    abstract updateAdjuster(adjusterId: number, adjuster:AdjusterUpdate) : Promise<Adjuster | undefined>
    abstract getClaimEncounters(claimId: number) : Promise<TealCompEncounterHeader[]>
    abstract getClaimsForParty(id: number) : Promise<TealCompClaim[]>

}



@Injectable({providedIn: 'root'})
export class ClaimService extends ClaimServiceBase
{



    constructor
    (
        private _api: ApiService,
        private _messages: MessageService,
        private _store: Store

    )
    {
        super();
    }

    override getClaim(claimId: number): Observable<TealCompClaim>
    {
        return this._api.makeAPIGetCall<TealCompClaim>(environment.harlowe.tealcomp.baseUri, "/claims/" + claimId.toString(), {})
        .pipe(map
            (
                c =>
                {
                    return instanceToInstance(c);
                }
            )
        )
    }

    override createClaim(claim: TealCompClaimInsert): Promise<TealCompClaim | undefined> 
    {
        return lastValueFrom
        (
            this._api.makeAPIPutCall<TealCompClaim>(environment.harlowe.tealcomp.baseUri,"/claims",claim)
              .pipe(map
                (
                    c => 
                        { 
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Created Claim ' + c.claimNumber}); 
                            let tcc = Object.assign(new TealCompClaim(c.id,c.claimNumber,c.party,new Date(c.claimDate),c.lineOfBusiness),c);
                            tcc.claimDate = new Date(c.claimDate);
                            return tcc;
                        } 
                ))
        );
    }

    override searchAdjusters(q: string): Promise<Adjuster[]> 
    {
        return lastValueFrom
        (
          this._api.makeAPIGetCall<Adjuster[]>(environment.harlowe.tealcomp.baseUri,"/adjusters",{query: q})
                .pipe(map(adjusters => adjusters.map(a => instanceToInstance(a))))
        );
    }
    override searchCarriers(q: string): Promise<Carrier[]> 
    {
        return lastValueFrom
        (
          this._api.makeAPIGetCall<Carrier[]>(environment.harlowe.tealcomp.baseUri,"/carriers",{query: q})
                  .pipe(map(carriers => carriers.map(c => {
                      const carrier = new Carrier(c.id, c.name);
                      carrier.companyLocation = c.companyLocation;
                      carrier.billingEmailAddress  = c.billingEmailAddress;
                      return carrier;
                  })))
        );
    }

    override createNewCarrier(carrier: CarrierInsert): Promise<Carrier | undefined> 
    {
      return lastValueFrom
            (
              this._api.makeAPIPutCall<Carrier>(environment.harlowe.tealcomp.baseUri,"/carriers",carrier,{})
                    .pipe(map(c => { this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Created Carrier ' + c.name}); return new Carrier(c.id,c.name);} ))
            );
    }

    override createNewAdjuster(adjuster: AdjusterInsert): Promise<Adjuster | undefined> 
    {
      return lastValueFrom
            (
              this._api.makeAPIPutCall<Adjuster>(environment.harlowe.tealcomp.baseUri,"/adjusters",adjuster,{})
                    .pipe
                    (
                        map(a => { this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Created Adjuster ' + a.firstName + ' ' + a.lastName}); return a;} )
                        ,catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Create Adjuster', detail: ' ' + error.message}); return of(undefined)})
                    )
            );
    }

    override updateClaim(claimId: number, claim: TealCompClaimUpdate): Promise<TealCompClaim | undefined> 
    {
        return lastValueFrom
        (
          this._api.makeAPIPatchCall<TealCompClaim>(environment.harlowe.tealcomp.baseUri,"/claims/"+claimId.toString(),claim,{})
                .pipe
                (
                    map
                    (
                        c => 
                        { 
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Claim ' + c.claimNumber}); 
                            this._store.dispatch(new Claims.ClaimServiceClaimUpdated({claim:c}))
                            return c;
                        } 
                    )
                    

                    ,catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Update Claim', detail: ' ' + error.message}); return of(undefined)})
                )
        );    
    }

    override updateCarrier(carrierId: number, carrier: CarrierUpdate): Promise<Carrier | undefined> 
    {
        return lastValueFrom
        (
          this._api.makeAPIPatchCall<Carrier>(environment.harlowe.tealcomp.baseUri,"/carriers/"+carrierId.toString(),carrier,{})
                .pipe
                (
                    map(c => 
                        { 
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Carrier ' + c.name}); 
                            this._store.dispatch(new Claims.ClaimServiceCarrierUpdated({carrier:c}));
                            return c;
                        } )
                    ,catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Update Carrier', detail: ' ' + error.message}); return of(undefined)})
                )
        );    
    }
    override updateAdjuster(adjusterId: number, adjuster: AdjusterUpdate): Promise<Adjuster | undefined> 
    {
        return lastValueFrom
        (
          this._api.makeAPIPatchCall<Adjuster>(environment.harlowe.tealcomp.baseUri,"/adjusters/"+adjusterId.toString(),adjuster,{})
                .pipe
                (
                    map(a => 
                        { 
                            this._messages.add({ key: 'main-alert', severity: 'success', summary: 'Success', detail: 'Updated Adjuster ' + a.firstName + ' ' + a.lastName}); 
                            this._store.dispatch(new Claims.ClaimServiceAdjusterUpdated({adjuster:a}));
                            return a;
                        })
                    ,catchError((error:ErrorMessage) =>  {this._messages.add({ key: 'main-alert', severity: 'error', summary: 'Could Not Update Adjuster', detail: ' ' + error.message}); return of(undefined)})
                )
        );
    }

    override getClaimEncounters(claimId: number): Promise<TealCompEncounterHeader[]> 
    {
        return lastValueFrom
        (
            this._api.makeAPIGetCall<TealCompEncounterHeader[]>(environment.harlowe.tealcomp.baseUri,"/claims/"+claimId.toString()+"/encounters")
        )
    }

    override getClaimsForParty(id: number): Promise<TealCompClaim[]> 
      {
        return lastValueFrom
              (this._api.makeAPIGetCall<TealCompClaim[]>(environment.harlowe.tealcomp.baseUri,"/parties/"+id.toString()+"/claims",{})
                .pipe
                (
                  map
                  (
                    claims => 
                      claims.map
                      (
                        c => 
                        {
                          let tcc = new TealCompClaim(c.id,c.claimNumber,c.party,new Date(c.claimDate),c.lineOfBusiness);
                          tcc = Object.assign(tcc,c);
                          tcc.claimDate = new Date(c.claimDate);
                          return tcc;
                        }
                      )
                  )
                )
              ) ;
      }


}